/**
 * 설비 정보 유효성 검증 유틸리티
 * 서버(MachineDocument.resolver.ts)의 검증 로직을 클라이언트에서 재현하여
 * 오프라인 모드에서도 데이터 무결성을 보장합니다.
 */

/** 검증 에러 결과 타입 */
export interface MachineInfoValidationError {
  field: string;
  message: string;
}

/** 텍스트 필드별 최대 길이 */
export const MACHINE_INFO_MAX_LENGTHS: Record<string, number> = {
  modelNo: 100,
  checkName: 50,
  position: 200,
  modelCreator: 100,
};

/** 필드 레이블 매핑 (에러 메시지용) */
const FIELD_LABELS: Record<string, string> = {
  modelNo: "관리번호",
  checkName: "점검자",
  position: "설치위치",
  modelCreator: "제조사",
};

/**
 * 설비 정보 필드 검증
 * @param fieldKey - 필드 키 (checkStartDate, modelNo 등)
 * @param value - 입력값 (dateRange인 경우 시작일)
 * @param endValue - dateRange인 경우 종료일
 * @returns 검증 에러 또는 null (통과)
 */
export function validateMachineInfoField(
  fieldKey: string,
  value: string,
  endValue?: string
): MachineInfoValidationError | null {
  // 날짜 범위 검증 (서버 로직과 동일)
  if (fieldKey === "checkStartDate" && endValue !== undefined) {
    return validateDateRange(value, endValue);
  }

  // 설치년도 범위 검증
  if (fieldKey === "modelYear" && value) {
    return validateModelYear(value);
  }

  // 텍스트 필드 길이 검증
  const maxLength = MACHINE_INFO_MAX_LENGTHS[fieldKey];
  if (maxLength && value && value.length > maxLength) {
    const label = FIELD_LABELS[fieldKey] || fieldKey;
    return {
      field: fieldKey,
      message: `${label}은(는) ${maxLength}자 이내로 입력해주세요.`,
    };
  }

  return null;
}

/** 날짜 범위 검증 (서버 MachineDocument.resolver.ts와 동일한 로직) */
function validateDateRange(startDate: string, endDate: string): MachineInfoValidationError | null {
  // 종료일만 있고 시작일이 없으면 에러
  if (endDate && !startDate) {
    return {
      field: "checkEndDate",
      message: "점검종료일을 설정하려면 점검시작일이 먼저 설정되어야 합니다.",
    };
  }

  // 종료일이 시작일보다 이전이면 에러
  if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
    return {
      field: "checkEndDate",
      message: "점검종료일은 점검시작일보다 이후여야 합니다.",
    };
  }

  return null;
}

/** 설치년도 합리적 범위 검증 */
function validateModelYear(dateStr: string): MachineInfoValidationError | null {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return null;

  const year = date.getFullYear();
  const currentYear = new Date().getFullYear();

  if (year < 1900 || year > currentYear + 5) {
    return {
      field: "modelYear",
      message: `설치년도는 1900년 ~ ${currentYear + 5}년 사이로 입력해주세요.`,
    };
  }

  return null;
}
